import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Trash2, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import api from '../services/api';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (e) {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: 1 })));
      setUnreadCount(0);
    } catch (e) {}
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications/clear');
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) {}
  };

  const severityIcon = (severity) => {
    switch (severity) {
      case 'error': return <XCircle size={16} className="text-red-400 flex-shrink-0" />;
      case 'warning': return <AlertTriangle size={16} className="text-yellow-400 flex-shrink-0" />;
      case 'success': return <CheckCircle size={16} className="text-green-400 flex-shrink-0" />;
      default: return <Info size={16} className="text-blue-400 flex-shrink-0" />;
    }
  };

  const timeAgo = (date) => {
    const mins = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
    if (mins < 1) return 'agora';
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-80 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="p-3 border-b border-gray-700 flex items-center justify-between">
            <span className="text-white font-semibold text-sm">Notificações</span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1" title="Marcar todas como lidas"><CheckCheck size={14} />Ler todas</button>
              )}
              {notifications.length > 0 && (
                <button onClick={clearAll} className="text-gray-500 hover:text-red-400" title="Limpar todas"><Trash2 size={14} /></button>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={32} className="mx-auto mb-2 text-gray-600" />
                <p className="text-gray-500 text-sm">Nenhuma notificação</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`p-3 border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer transition-colors ${!n.isRead ? 'bg-blue-900/10 border-l-2 border-l-blue-500' : ''}`}
                  onClick={async () => {
                    if (!n.isRead) {
                      await api.post(`/notifications/read/${n.id}`);
                      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, isRead: 1 } : x));
                      setUnreadCount(prev => Math.max(0, prev - 1));
                    }
                    if (n.link) window.location.href = n.link;
                  }}
                >
                  <div className="flex items-start gap-2">
                    {severityIcon(n.severity)}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${!n.isRead ? 'text-white font-medium' : 'text-gray-300'}`}>{n.title}</p>
                      {n.message && <p className="text-gray-500 text-xs mt-1 line-clamp-2">{n.message}</p>}
                    </div>
                    <span className="text-gray-600 text-xs flex-shrink-0">{timeAgo(n.createdAt)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
